/** WebSocket client — auto-reconnects with backoff, visibility-aware, heartbeat. */

import type { WSMessage } from "@/types";
import { decodeWire } from "@/api/wire";

type MessageHandler = (msg: WSMessage) => void;
type StatusHandler = (connected: boolean) => void;

interface SocketAuth {
  getToken: () => Promise<string | null>;
  seat?: () => number | undefined;
}

export class ScorioSocket {
  private ws: WebSocket | null = null;
  private handlers: Set<MessageHandler> = new Set();
  private statusHandlers: Set<StatusHandler> = new Set();
  private closeHandlers: Set<(code: number) => void> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private url: string;
  private auth: SocketAuth | undefined;
  private authRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldConnect = false;
  private _connected = false;
  private retryCount = 0;
  private boundVisibility: (() => void) | null = null;
  private boundOnline: (() => void) | null = null;

  constructor(url: string, auth?: SocketAuth) {
    this.url = url;
    this.auth = auth;
  }

  get connected(): boolean {
    return this._connected;
  }

  connect(): void {
    if (this.shouldConnect) return;
    this.shouldConnect = true;
    this.retryCount = 0;
    this.doConnect();

    // Reconnect when tab becomes visible or device comes back online
    this.boundVisibility = () => {
      if (document.visibilityState === "visible" && this.shouldConnect && !this.ws) {
        this.retryCount = 0;
        this.doConnect();
      }
    };
    this.boundOnline = () => {
      if (this.shouldConnect && !this.ws) {
        this.retryCount = 0;
        this.doConnect();
      }
    };
    document.addEventListener("visibilitychange", this.boundVisibility);
    window.addEventListener("online", this.boundOnline);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  onClose(handler: (code: number) => void): () => void {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  private setConnected(value: boolean): void {
    if (this._connected !== value) {
      this._connected = value;
      this.statusHandlers.forEach((h) => h(value));
    }
  }

  disconnect(): void {
    this.shouldConnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    this.stopAuthRefresh();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setConnected(false);
    if (this.boundVisibility) {
      document.removeEventListener("visibilitychange", this.boundVisibility);
      this.boundVisibility = null;
    }
    if (this.boundOnline) {
      window.removeEventListener("online", this.boundOnline);
      this.boundOnline = null;
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private doConnect(): void {
    if (!this.shouldConnect) return;
    // Don't open a new socket if one is already connecting/open
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)
    )
      return;

    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.setConnected(false);
      this.scheduleReconnect();
      return;
    }

    const socket = this.ws;
    socket.onopen = () => {
      if (this.auth) {
        void this.authenticate(socket).catch(() => socket.close());
      } else {
        this.retryCount = 0;
        this.setConnected(true);
        this.startHeartbeat();
      }
    };

    socket.onmessage = (event) => {
      if (this.ws !== socket) return;
      try {
        const data = decodeWire(JSON.parse(event.data as string)) as {
          type: string;
          expires_in?: number;
        };
        if (data.type === "auth_ok" && this.auth) {
          this.retryCount = 0;
          this.setConnected(true);
          this.startHeartbeat();
          this.stopAuthRefresh();
          const refreshMs = Math.max(1000, ((data.expires_in ?? 3600) - 60) * 1000);
          this.authRefreshTimer = setTimeout(() => {
            void this.authenticate(socket).catch(() => socket.close());
          }, refreshMs);
          return;
        }
        if (this.auth && !this.connected) return;
        this.handlers.forEach((h) => h(data as WSMessage));
      } catch {
        // Ignore malformed messages.
      }
    };

    socket.onclose = (event) => {
      if (this.ws !== socket) return;
      this.ws = null;
      this.setConnected(false);
      this.stopHeartbeat();
      this.stopAuthRefresh();
      this.closeHandlers.forEach((handler) => handler(event.code));
      if (event.code === 4409) {
        // A revoked/taken-over seat must not fight the winning device.
        this.shouldConnect = false;
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  private async authenticate(socket: WebSocket): Promise<void> {
    const token = await this.auth?.getToken();
    if (!this.shouldConnect || this.ws !== socket || socket.readyState !== WebSocket.OPEN) return;
    if (!token) {
      socket.close();
      return;
    }
    const seat = this.auth?.seat?.();
    socket.send(JSON.stringify({ type: "auth", token, ...(seat !== undefined ? { seat } : {}) }));
  }

  private stopAuthRefresh(): void {
    if (this.authRefreshTimer) clearTimeout(this.authRefreshTimer);
    this.authRefreshTimer = null;
  }

  private scheduleReconnect(): void {
    if (!this.shouldConnect || this.reconnectTimer) return;
    // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.retryCount), 30_000);
    this.retryCount++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    // Send a ping every 25s to keep the connection alive
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send("ping");
        } catch {
          // Connection likely dead — close will trigger reconnect
          this.ws?.close();
        }
      }
    }, 25_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}
